import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { trackVisit } from "@/lib/api";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import AuthModal from "@/components/AuthModal";
import HomePage from "@/pages/HomePage";
import CategoryPage from "@/pages/CategoryPage";
import ArticlePage from "@/pages/ArticlePage";
import SearchPage from "@/pages/SearchPage";
import HistoryPage from "@/pages/HistoryPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import AuthCallback from "@/pages/AuthCallback";
import ProfilePage from "@/pages/ProfilePage";
import AdminPage from "@/pages/admin/AdminPage";
// import PricingPage from "@/pages/PricingPage"; // HIDDEN: re-enable when ready
import AboutPage from "@/pages/AboutPage";
import TermsPage from "@/pages/TermsPage";
import ContactPage from "@/pages/ContactPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import NewsletterConfirmPage from "@/pages/NewsletterConfirmPage";
import NewsletterUnsubscribePage from "@/pages/NewsletterUnsubscribePage";
import SEOStudioPage from "@/pages/SEOStudioPage";
import AIToolsPage from "@/pages/AIToolsPage";
import AIToolDetailPage from "@/pages/AIToolDetailPage";
import AdminPortal from "@/admin-portal/AdminPortal";
import TrendingAIPage from "@/pages/TrendingAIPage";
import AINewsPage from "@/pages/ai/AINewsPage";
import AIModelsPage from "@/pages/ai/AIModelsPage";
import AIAgentsPage from "@/pages/ai/AIAgentsPage";
import AIResearchPage from "@/pages/ai/AIResearchPage";
import AICompaniesPage from "@/pages/ai/AICompaniesPage";
import AIStartupsPage from "@/pages/ai/AIStartupsPage";
import AIFundingPage from "@/pages/ai/AIFundingPage";
import AICodingPage from "@/pages/ai/AICodingPage";
import AIEventsPage from "@/pages/ai/AIEventsPage";
import AILearningPage from "@/pages/ai/AILearningPage";
import AIBenchmarksPage from "@/pages/ai/AIBenchmarksPage";
import AIMarketplacePage from "@/pages/ai/AIMarketplacePage";
import AIItemDetailPage from "@/pages/ai/AIItemDetailPage";
import GoRedirectPage from "@/pages/GoRedirectPage";
import ErrorBoundary from "@/components/ErrorBoundary";

function RequireAdmin({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="p-10 text-zinc-500 font-mono-plex text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function RequireAuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-zinc-500 font-mono-plex text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const tracked = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Fire once per app load — dedup is handled server-side
  useEffect(() => {
    if (!tracked.current) {
      tracked.current = true;
      trackVisit(location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Emergent Google Auth callback: session_id in URL hash
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        {/* Primary SEO-friendly route */}
        <Route path="/news/:slug" element={<ArticlePage />} />
        {/* Legacy GUID route — ArticlePage will redirect to /news/:slug automatically */}
        <Route path="/article/:id" element={<ArticlePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/history" element={<RequireAuthRoute><HistoryPage /></RequireAuthRoute>} />
        <Route path="/profile" element={<RequireAuthRoute><ProfilePage /></RequireAuthRoute>} />
        <Route path="/admin" element={<RequireAdmin><AdminPage /></RequireAdmin>} />
        {/* <Route path="/pricing" element={<PricingPage />} /> */}{/* HIDDEN: re-enable when ready */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/payment/success" element={<RequireAuthRoute><PaymentSuccessPage /></RequireAuthRoute>} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/newsletter/confirm" element={<NewsletterConfirmPage />} />
        <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
        <Route path="/seo-studio" element={<SEOStudioPage />} />

        {/* AI Tools */}
        <Route path="/ai-tools" element={<AIToolsPage />} />
        <Route path="/ai-tools/trending" element={<AIToolsPage />} />
        <Route path="/ai-tools/new" element={<AIToolsPage />} />
        <Route path="/ai-tools/categories" element={<AIToolsPage />} />
        <Route path="/ai-tools/:slug" element={<AIToolDetailPage />} />

        {/* Affiliate redirect — shows "Taking you to..." screen */}
        <Route path="/go/:slug" element={<GoRedirectPage />} />

        {/* Trending AI — canonical route is /trending; /trending-ai kept for back-compat */}
        <Route path="/trending" element={<ErrorBoundary section="AI Tracker"><TrendingAIPage /></ErrorBoundary>} />
        <Route path="/trending-ai" element={<ErrorBoundary section="AI Dashboard"><TrendingAIPage /></ErrorBoundary>} />
        <Route path="/trending-ai/news" element={<ErrorBoundary section="AI News"><AINewsPage /></ErrorBoundary>} />
        <Route path="/trending-ai/models" element={<ErrorBoundary section="AI Models"><AIModelsPage /></ErrorBoundary>} />
        <Route path="/trending-ai/agents" element={<ErrorBoundary section="AI Agents"><AIAgentsPage /></ErrorBoundary>} />
        <Route path="/trending-ai/research" element={<ErrorBoundary section="AI Research"><AIResearchPage /></ErrorBoundary>} />
        <Route path="/trending-ai/companies" element={<ErrorBoundary section="AI Companies"><AICompaniesPage /></ErrorBoundary>} />
        <Route path="/trending-ai/startups" element={<ErrorBoundary section="AI Startups"><AIStartupsPage /></ErrorBoundary>} />
        <Route path="/trending-ai/funding" element={<ErrorBoundary section="AI Funding"><AIFundingPage /></ErrorBoundary>} />
        <Route path="/trending-ai/coding" element={<ErrorBoundary section="AI Coding"><AICodingPage /></ErrorBoundary>} />
        <Route path="/trending-ai/events" element={<ErrorBoundary section="AI Events"><AIEventsPage /></ErrorBoundary>} />
        <Route path="/trending-ai/learning" element={<ErrorBoundary section="AI Learning"><AILearningPage /></ErrorBoundary>} />
        <Route path="/trending-ai/benchmarks" element={<ErrorBoundary section="AI Benchmarks"><AIBenchmarksPage /></ErrorBoundary>} />
        <Route path="/trending-ai/marketplace" element={<ErrorBoundary section="AI Marketplace"><AIMarketplacePage /></ErrorBoundary>} />
        <Route path="/trending-ai/:section/:slug" element={<ErrorBoundary section="Item Detail"><AIItemDetailPage /></ErrorBoundary>} />
      </Route>

      {/* Admin Portal — own layout, fully outside the public <Layout> */}
      <Route
        path="/admin-portal/*"
        element={<RequireAdmin><AdminPortal /></RequireAdmin>}
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <AuthModal />
          <Toaster
            theme="light"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                color: "#0f172a",
                borderRadius: "8px",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
